import { SupportSettingsKey, isSupportSettingsKey } from "../utility/supportSettings";
import { logger } from "../utility/logger";
import { currentRequestScope, RequestScope } from "./ApiService";
import { sameRequestScope } from "./requestScope";

/**
 * A ONE-SHOT INTENT QUEUE between the support assistant's create-form chips and the settings
 * screen that hosts the form.
 *
 * The chip cannot fill the form in itself: the state it has to write lives in `pages/UsersPage`
 * or `pages/OperationsPage`, which are a route away. So the chip leaves the values here and
 * navigates; the page picks them up once its own data has loaded.
 *
 * ⚠️ MODULE STATE, NOT A STORE SLICE — AND THAT IS THE POINT.
 * The easy-peasy store is persisted to `sessionStorage` (`store/index.ts`). An intent that
 * survived a reload would fill a form in that nobody asked for, on a screen somebody arrived at
 * for some other reason, possibly days later — and unlike a stray modal, a filled-in form is
 * something a person can then save without ever knowing where the values came from. There is
 * nothing here worth persisting and a great deal worth NOT persisting. This follows the house
 * pattern for runtime-only state — `services/supportActionBus.ts`, `services/regionRuntime.ts`.
 *
 * ⚠️ AND IT CARRIES VALUES, WHICH IS THE ONE WAY IT DIFFERS FROM `supportActionBus`.
 * That bus deliberately carries no payload beyond a key and a job number, because the modal it
 * opens would SUBMIT what it was given. This one carries a dictionary of strings — and the
 * reason that is safe is not this file: it is that the only thing on the other end is
 * `setState`, that no save function is reachable from any of it, and that the person still
 * presses **Update**. See the hooks, and the two `neverSaves` tests that drive them.
 */

export interface SettingsPrefillIntent {
  /** Which form to fill in. Resolved through `utility/supportSettings`, never called directly. */
  readonly key: SupportSettingsKey;

  /**
   * The values to put in the boxes, by field name.
   *
   * Already twice-filtered by the time it gets here — against the Function App's schema and
   * against this build's own field list — and frozen, because a page hook holding it across a
   * render must not be able to change what a later claimant would have seen.
   */
  readonly fields: Readonly<Record<string, string>>;

  /** `Date.now()` when the chip was clicked. Drives the TTL below. */
  readonly requestedAt: number;

  /**
   * The organisation the chip was clicked in. The values describe a user, a resource or a
   * filter there, so they are handed over only in the same one (compared as the access
   * provider compares scopes), and dropped when the organisation changes
   * (`dropSettingsPrefill`, called by `applySupportTarget`).
   */
  readonly scope: RequestScope;
}

export type SettingsPrefillListener = (intent: SettingsPrefillIntent) => void;

/**
 * How long an unclaimed intent stays live.
 *
 * It exists to cover ONE navigation plus the settings screen's own load — Users fetches users,
 * warehouses and resources before its form settles. Thirty seconds is generous for that and far
 * too short to survive a user wandering off, which is the failure this bounds: an intent with no
 * expiry would sit in memory for the whole session and fill a form in the moment somebody opened
 * User Settings for an unrelated reason, with no conversation on screen to explain it.
 *
 * The same number as `JOB_ACTION_TTL_MS`, and deliberately a separate constant: the two buses
 * bound different journeys and there is no reason for a change to one to silently move the other.
 */
export const SETTINGS_PREFILL_TTL_MS = 30_000;

let pending: SettingsPrefillIntent | null = null;
let listeners: SettingsPrefillListener[] = [];

/**
 * Record an intent, replacing any earlier one.
 *
 * Replacing rather than queueing is deliberate, for the same reason as the action bus: there is
 * one user, they pressed one chip most recently, and that is the thing they are waiting for. A
 * queue would fill a second form in behind the first.
 */
export function requestSettingsPrefill(intent: {
  key: string;
  fields: Readonly<Record<string, string>>;
}): void {
  if (!isSupportSettingsKey(intent?.key)) {
    // Nothing could ever claim it — the pages claim by key. Dropping it loudly beats leaving a
    // permanently unclaimable intent parked in the module.
    logger.warn("supportSettingsBus: refused an intent with an unknown form key.", intent?.key);
    return;
  }

  const fields = { ...(intent.fields ?? {}) };

  if (Object.keys(fields).length === 0) {
    // A prefill with nothing in it would navigate somewhere, press Add, and leave an empty row
    // the user then has to delete. Worse than no chip at all.
    logger.warn("supportSettingsBus: refused an intent with no fields.", intent.key);
    return;
  }

  pending = {
    key: intent.key,
    fields: Object.freeze(fields),
    requestedAt: Date.now(),
    scope: currentRequestScope(),
  };

  // Copied before iterating: a listener that unsubscribes itself would otherwise reindex the
  // array mid-loop and skip its neighbour.
  [...listeners].forEach((listener) => {
    try {
      listener(pending as SettingsPrefillIntent);
    } catch (error) {
      logger.warn("supportSettingsBus: a listener threw.", error);
    }
  });
}

/**
 * Take the pending intent, if it is for one of the forms `kind` names.
 *
 * ⚠️ ONE SHOT. A successful claim CLEARS the intent before the caller has looked at it, which is
 * what makes it safe for a page to claim BEFORE it runs its own guards: an intent the guards
 * then refuse cannot come back and fire later, on a re-render, on a refetch, or after the user
 * has changed something that makes the guard pass.
 *
 * `kind` takes one key or several because `pages/OperationsPage` hosts two of the three forms —
 * a resource and a filter — on one page. A mismatch is NOT a claim: an intent meant for User
 * Settings survives Resource Settings untouched.
 */
export function claimSettingsPrefill(
  kind: SupportSettingsKey | readonly SupportSettingsKey[]
): SettingsPrefillIntent | null {
  if (!pending) return null;

  if (Date.now() - pending.requestedAt > SETTINGS_PREFILL_TTL_MS) {
    // Expired intents are dropped on the first look at them, whoever is looking. Leaving a stale
    // one in place would let it expire again for the next caller and the one after.
    pending = null;
    return null;
  }

  if (!sameRequestScope(pending.scope, currentRequestScope())) {
    pending = null;
    return null;
  }

  const wanted = Array.isArray(kind) ? kind : [kind as SupportSettingsKey];
  if (!wanted.includes(pending.key)) return null;

  const claimed = pending;
  pending = null;
  return claimed;
}

/**
 * Be told when an intent is recorded.
 *
 * Needed for the case navigation cannot cover: the user is ALREADY on the settings screen the
 * chip names, so `navigate` is a no-op, nothing remounts, and no effect dependency changes.
 * Without this the chip would appear to do nothing at all on exactly the screen where somebody
 * asking how to add a user is most likely to be standing.
 *
 * Returns the unsubscribe function, so it drops straight out of a `useEffect`.
 */
export function subscribeSettingsPrefill(listener: SettingsPrefillListener): () => void {
  listeners.push(listener);

  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

/** Drop the pending prefill. `applySupportTarget` calls it when the organisation changes. */
export function dropSettingsPrefill(): void {
  pending = null;
}

/** Test seam. Drops the pending intent and every listener. */
export function __resetSupportSettingsBus(): void {
  pending = null;
  listeners = [];
}
